<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps({
  visible: { type: Boolean, required: true },
  note: { type: Object, default: null },
});

const emit = defineEmits(['update:visible', 'edit', 'deleted', 'refresh']);

const show = ref(props.visible);
const localNote = ref(props.note);
const loading = ref(false);
const toast = useToast();
const confirm = useConfirm();

const fetchNote = async (uuid) => {
    if (!uuid) return;
    loading.value = true;
    try {
        const { data } = await axios.get(`/ajax/admin/notes/${uuid}`);
        localNote.value = data.data;
    } catch (e) {
        console.error('Failed to fetch note details:', e);
    } finally {
        loading.value = false;
    }
};

watch(
    () => [props.visible, props.note?.uuid || props.note?.id],
    (newVal, oldVal) => {
        const [isVisible, uuid] = newVal;
        const [oldVisible, oldUuid] = oldVal || [false, null];
        
        show.value = isVisible;
        if (isVisible && uuid) {
            if (!oldVisible || uuid !== oldUuid) {
                localNote.value = props.note;
                fetchNote(uuid);
            }
        }
    },
    { immediate: true }
);

  watch(show, (v) => {
    emit('update:visible', v);
  });

const formatTime = (dt) => {
  if (!dt) return '--:--';
  // Normalize ISO T to space and strip timezone/milliseconds
  let str = String(dt).replace('T', ' ');
  if (str.includes(' ')) {
    return str.split(' ')[1].substring(0, 5);
  }
  return new Date(dt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dt) => {
    if (!dt) return '';
    let str = String(dt).replace('T', ' ');
    if (str.includes(' ')) {
        const [y, m, d] = str.split(' ')[0].split('-');
        return `${d}.${m}.${y}`;
    }
    return new Date(dt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const deleteNote = () => {
    confirm.require({
        message: 'Möchten Sie diese Notiz wirklich löschen?',
        header: 'Notiz löschen',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Abbrechen',
        acceptLabel: 'Löschen',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
            try {
                const uuid = localNote.value.uuid || localNote.value.id;
                await axios.delete(`/ajax/admin/notes/${uuid}`);
                toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Notiz gelöscht', life: 3000 });
                emit('deleted');
                show.value = false;
            } catch (e) {
                toast.add({ severity: 'error', summary: 'Fehler', detail: 'Löschen fehlgeschlagen', life: 5000 });
            }
        }
    });
};
</script>

<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Notiz Details" 
    :style="{ width: '400px' }"
    :pt="{
      header: { class: '!p-5 !border-b !border-gray-100 !items-center' },
      title: { class: '!text-base !font-extrabold !text-gray-900 !uppercase !tracking-tight' }
    }"
  >
    <div v-if="loading && !localNote" class="p-12 flex justify-center">
       <i class="pi pi-spin pi-spinner text-3xl text-gray-300"></i>
    </div>

    <div v-else-if="localNote" class="flex flex-col h-full">
      <!-- Header / Type Info -->
      <div class="p-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div 
            class="w-4 h-4 rounded-full" 
            :style="{ backgroundColor: localNote.type?.color || '#ddd' }"
          ></div>
          <span class="font-bold text-gray-700 tracking-tight">{{ localNote.type?.name || 'Allgemeine Notiz' }}</span>
        </div>
        <Tag v-if="localNote.is_time_blocked" value="ZEIT BLOCKIERT" severity="danger" class="!text-[9px] !px-2 !py-0.5" />
      </div>

      <!-- Main Content -->
      <div class="p-4 space-y-4">
        <!-- Note Content -->
        <div class="bg-yellow-50/50 p-3 rounded-xl border border-yellow-100/50">
           <p class="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
             {{ localNote.note || 'Kein Inhalt' }}
           </p>
        </div>

        <div class="grid grid-cols-2 gap-4">
           <!-- Employee -->
           <div class="flex flex-col gap-1.5">
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Mitarbeiter</span>
              <div class="flex items-center gap-2">
                <div class="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                  <i class="pi pi-user text-[10px]"></i>
                </div>
                <span class="text-xs font-bold text-gray-700">{{ localNote.provider?.name || 'Kein Mitarbeiter' }}</span>
              </div>
           </div>

           <!-- Time -->
           <div class="flex flex-col gap-1.5">
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Zeitraum</span>
              <div class="flex flex-col">
                <span class="text-xs font-bold text-gray-700">{{ formatTime(localNote.start_datetime) }} - {{ formatTime(localNote.end_datetime) }}</span>
                <span class="text-[10px] text-gray-400 font-medium">{{ formatDate(localNote.start_datetime) }}</span>
              </div>
           </div>
        </div>
      </div>

      <div class="p-3 bg-gray-50/50 border-t border-gray-100 flex justify-between items-center mt-auto">
        <button 
          type="button"
          @click="show = false" 
          class="text-gray-500 font-bold hover:!bg-sky-50 hover:!text-sky-600 transition-all px-6 py-2 rounded-full text-xs"
        >
          Schließen
        </button>
        
        <div class="flex gap-3 items-center">
          <Button 
            label="Löschen" 
            icon="pi pi-trash" 
            class="p-button-danger p-button-text !text-[11px] !font-bold hover:!bg-red-50 !rounded-full !px-4" 
            @click="deleteNote" 
          />
          <Button 
            label="Bearbeiten" 
            icon="pi pi-pencil" 
            class="!bg-black !text-[#FFF053] !border-none !rounded-xl !px-6 !py-2 !text-xs !font-bold shadow-sm" 
            @click="emit('edit', localNote)" 
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>


